import React, { useState, useEffect } from 'react';
import axios from "axios";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../firebase";
import ResultsDashboard from "../components/ResultsDashboard";
import LoadingScreen from "../components/LoadingScreen";
import PaywallModal from "../components/PaywallModal";
import { 
  ShieldCheck, Target, Zap, TrendingUp, Check, Plus, 
  MessageSquareText, Lock, CreditCard, IndianRupee, Sparkles, ArrowRight 
} from "lucide-react";
import "../App.css";

// 1. API CONFIGURATION
const API = "https://site--siftcard--gsjgj7qfyxbd.code.run/api";

// 2. CONSTANTS
const POPULAR_CARDS = ["HDFC Infinia", "HDFC Regalia Gold", "Axis Magnus", "Axis Atlas", "SBI Cashback", "ICICI Amazon Pay", "Amex Plat Travel", "HSBC Live+"];
const SPEND_TIERS = ["₹25k - ₹50k", "₹50k - ₹1L", "₹1L - ₹2.5L", "₹2.5L+"];
const GOALS = ["Max Cashback", "Free Business Class", "Luxury Hotels", "Zero Annual Fees"];

const Home = () => {
  const [view, setView] = useState("landing");
  const [analysisResult, setAnalysisResult] = useState(null);
  const [email, setEmail] = useState("");
  const [paywallActive, setPaywallActive] = useState(false);
  
  const [anonCount, setAnonCount] = useState(0);
  const [selectedCards, setSelectedCards] = useState([]);
  const [selectedSpend, setSelectedSpend] = useState("");
  const [selectedGoal, setSelectedGoal] = useState("");
  const [userInput, setUserInput] = useState("");

  // Load anonymous search count
  useEffect(() => {
    const savedCount = localStorage.getItem("sift_anon_count") || 0;
    setAnonCount(parseInt(savedCount));
  }, []);

  // Update prompt box automatically based on selections
  useEffect(() => {
    const cardText = selectedCards.length > 0 ? `I own ${selectedCards.join(", ")}.` : "";
    const spendText = selectedSpend ? ` My monthly spend is ${selectedSpend}.` : "";
    const goalText = selectedGoal ? ` My main goal is ${selectedGoal}.` : "";
    const fullPrompt = `${cardText}${spendText}${goalText}`.trim();
    
    if (fullPrompt.length > 0) {
      setUserInput(fullPrompt);
    }
  }, [selectedCards, selectedSpend, selectedGoal]);

  const handleAnalyze = async (userEmail = "") => {
    setView("loading");
    try {
      const response = await axios.post(`${API}/optimize`, {
        user_input: userInput,
        email: userEmail, 
      });
      
      if (!userEmail) {
        const newCount = anonCount + 1;
        setAnonCount(newCount);
        localStorage.setItem("sift_anon_count", newCount);
      }

      setAnalysisResult(response.data);
      setView("results");
    } catch (err) {
      setView("landing");
      if (err.response?.status === 402) {
        setPaywallActive(true);
      } else {
        alert("Something went wrong. Please try again.");
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      setEmail(result.user.email);
      handleAnalyze(result.user.email);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const getButtonState = () => {
    if (userInput.length < 10) return { text: "Select your cards to start...", disabled: true, action: null };
    if (!email && anonCount < 2) return { text: `GENERATE MY FREE EFFICIENCY REPORT`, disabled: false, action: () => handleAnalyze("") };
    if (!email && anonCount >= 2) return { text: "SIGN IN TO REVEAL RESULTS", disabled: false, action: handleGoogleLogin, icon: <Lock className="w-5 h-5" /> };
    return { text: "GENERATE MY EFFICIENCY REPORT", disabled: false, action: () => handleAnalyze(email) };
  };

  const btn = getButtonState();

  return (
    <div className="bg-[#0b0d17] min-h-screen text-white font-sans">
      {/* 1. LANDING VIEW */}
      {view === "landing" && (
        <div className="max-w-4xl mx-auto pt-16 px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold mb-6">
              <Zap className="w-3 h-3 fill-current" />
              AI AUDIT REVEALS HIDDEN REWARDS
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[1.1]">
              The <span className="text-emerald-400">Keep or Kill</span> <br /> 
              Credit Card List.
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              {anonCount < 2 
                ? "Stop wondering if you have the right cards. Get a strategic audit of your portfolio and discover your potential annual yield. Your first 2 scans are on the house." 
                : "Your free preview is over. Sign in with Google to unlock more audits."}
            </p>
          </div>

          {/* STRATEGIC INSIGHT BOX */}
          <div className="relative group max-w-2xl mx-auto mt-12 mb-10">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-yellow-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
            <div className="relative flex items-start gap-4 p-5 bg-[#0b0d17] border border-amber-500/20 rounded-2xl shadow-2xl">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>
              <div className="text-left">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-500/80 mb-1">Strategic Tip</p>
                <p className="text-sm text-slate-300 leading-relaxed">
                  For a precise audit, include your monthly category spends (e.g., "I spend 40k on Amazon and 10k on Swiggy"). 
                  The AI uses this to calculate your **actual efficiency**. Missing a card? Just type it below.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-10">
            {/* STEP 1: CARDS */}
            <section>
              <div className="flex items-center gap-2 mb-4 text-blue-400">
                <CreditCard className="w-4 h-4" />
                <h3 className="font-bold text-sm uppercase tracking-widest">Step 1: Your Current Cards</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {POPULAR_CARDS.map(card => (
                  <button
                    key={card}
                    onClick={() => setSelectedCards(prev => prev.includes(card) ? prev.filter(c => c !== card) : [...prev, card])}
                    className={`p-4 rounded-2xl border text-xs font-bold transition-all flex items-center justify-between ${
                      selectedCards.includes(card) ? 'bg-blue-600 border-blue-400' : 'bg-slate-800/40 border-slate-700 hover:border-slate-500'
                    }`}
                  >
                    {card}
                    {selectedCards.includes(card) ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3 opacity-20" />}
                  </button>
                ))}
              </div>
            </section>

            {/* STEP 2 & 3 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <section>
                <div className="flex items-center gap-2 mb-4 text-emerald-400">
                  <IndianRupee className="w-4 h-4" />
                  <h3 className="font-bold text-sm uppercase tracking-widest">Step 2: Monthly Spend</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {SPEND_TIERS.map(tier => (
                    <button key={tier} onClick={() => setSelectedSpend(tier)} className={`p-3 rounded-xl border text-xs font-bold transition-all ${selectedSpend === tier ? 'bg-emerald-600 border-emerald-400' : 'bg-slate-800/40 border-slate-700'}`}>{tier}</button>
                  ))}
                </div>
              </section>
              <section>
                <div className="flex items-center gap-2 mb-4 text-purple-400">
                  <Target className="w-4 h-4" />
                  <h3 className="font-bold text-sm uppercase tracking-widest">Step 3: Your Goal</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {GOALS.map(goal => (
                    <button key={goal} onClick={() => setSelectedGoal(goal)} className={`p-3 rounded-xl border text-xs font-bold transition-all ${selectedGoal === goal ? 'bg-purple-600 border-purple-400' : 'bg-slate-800/40 border-slate-700'}`}>{goal}</button>
                  ))}
                </div>
              </section>
            </div>

            {/* STEP 4: LIVE PROMPT */}
            <section className="pt-6">
              <div className="flex items-center gap-2 mb-4 text-blue-400">
                <MessageSquareText className="w-4 h-4" />
                <h3 className="font-bold text-sm uppercase tracking-widest">Your Live Audit Prompt</h3>
              </div>
              <div className="relative group">
                <textarea
                  className="w-full bg-slate-900 border-2 border-slate-800 rounded-3xl p-6 text-slate-200 placeholder:text-slate-700 outline-none focus:border-blue-500/50 transition-all font-mono text-sm leading-relaxed"
                  rows="4"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  placeholder="Select cards above or type your own here..."
                />
              </div>
            </section>

            {/* ACTION BUTTON */}
            <div className="pt-4 pb-20 text-center">
              <button
                onClick={btn.action}
                disabled={btn.disabled}
                className={`w-full py-6 font-black text-xl rounded-3xl flex items-center justify-center gap-4 transition-all shadow-2xl active:scale-[0.98] ${
                  btn.disabled ? 'bg-slate-800 text-slate-600 cursor-not-allowed' : 'bg-white text-black hover:scale-[1.01]'
                }`}
              >
                {btn.icon && btn.icon}
                {btn.text}
                {!btn.disabled && <ArrowRight className="w-5 h-5" />}
              </button>
              
              <div className="flex items-center justify-center gap-6 mt-6 opacity-30">
                <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest"><ShieldCheck className="w-3 h-3" /> Secure Audit</div>
                <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest"><Target className="w-3 h-3" /> AI Optimized</div>
                <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest"><TrendingUp className="w-3 h-3" /> Profit First</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. LOADING & RESULTS VIEWS */}
      <div className="max-w-7xl mx-auto px-4">
        {view === "loading" && <LoadingScreen />}
        {view === "results" && analysisResult && (
          <div className="py-10">
            <ResultsDashboard result={analysisResult} onReset={() => setView("landing")} />
          </div>
        )}
      </div>

      {paywallActive && (
        <PaywallModal onClose={() => setPaywallActive(false)} userEmail={email} />
      )}

      {/* 3. BETA FOOTER */}
      <div className="mt-20 py-10 border-t border-white/5 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 mb-4">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest text-nowrap">Public Beta</span>
        </div>
        <p className="text-sm text-white/40 max-w-md mx-auto leading-relaxed px-4">
          SiftCard is in active development. We are refining our reward algorithms daily to ensure accuracy across 200+ Indian credit cards.
        </p>
        <div className="mt-8">
          <p className="text-[10px] text-white/20 uppercase tracking-[0.2em] mb-3">Feedback & Feature Requests</p>
          <a href="mailto:siftcard@seedmind.in" className="group inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-all font-medium">
            <span className="underline underline-offset-4 decoration-white/10 group-hover:decoration-white/40 transition-all">siftcard@seedmind.in</span>
          </a>
        </div>
      </div>
    </div>
  );
};

export default Home;
